import { emptyGuid } from "@insite/client-framework/Common/StringHelpers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getCategoriesDataView, getSubCategoryIds } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadCategories from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategories";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Accordion from "@midland-industries/MobiusOverrides/Accordion/Accordion";
import { CustomDynamicFormState } from "@midland-industries/Store/Data/DynamicForms/DynamicFormReducer";
import setDynamicFormFieldValue from "@midland-industries/Store/Data/DynamicForms/Handlers/SetDynamicFormFieldValue";
import {
    DynamicFormButtonShapes,
    DynamicFormButtonTypes,
    DynamicFormButtonVariants,
    DynamicFormCheckboxGroupLabelPositionsRight,
    DynamicFormCheckboxLabelPositionsRight,
    DynamicFormEmailRegExp,
    DynamicFormTextFieldLabelPositionsTop,
    DynamicFormThirdPartyApiTypes,
    DynamicFormThirdPartyApiTypesCustom,
} from "@midland-industries/Types/DynamicFormModels";
import { determineFormIndex } from "@midland-industries/Utilities/DetermineFormIndex";
import DynamicForm from "@midland-industries/Widgets/DynamicForms/DynamicForm";
import DynamicFormCheckbox from "@midland-industries/Widgets/DynamicForms/DynamicFormCheckbox";
import DynamicFormCheckboxGroup from "@midland-industries/Widgets/DynamicForms/DynamicFormCheckboxGroup";
import DynamicFormRadioGroup from "@midland-industries/Widgets/DynamicForms/DynamicFormRadioGroup";
import DynamicFormTextField from "@midland-industries/Widgets/DynamicForms/DynamicFormTextField";
import React, { useEffect, useState } from "react";
import { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    emailRecipients = "emailRecipients",
    successMessage = "successMessage",
    columns = "columns",
    enableRecaptcha = "enableRecaptcha",
    submitButtonText = "submitButtonText",
    buttonVariant = "buttonVariant",
    buttonShape = "buttonShape",
    buttonType = "buttonType",
    sendToThirdPartyApi = "sendToThirdPartyApi",
    sendToApiType = "sendToApiType",
    customApiEndpoint = "customApiEndpoint",
    endpointRequiresAuth = "endpointRequiresAuth",
    endpointUserId = "endpointUserId",
    endpointPassword = "endpointPassword",
    displayCustomApiResponse = "displayCustomApiResponse",
    enableNewPricing = "enableNewPricing",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.emailRecipients]: string;
        [fields.successMessage]: string;
        [fields.columns]: number;
        [fields.enableRecaptcha]: boolean;
        [fields.submitButtonText]: string;
        [fields.buttonVariant]: DynamicFormButtonVariants;
        [fields.buttonShape]: DynamicFormButtonShapes;
        [fields.buttonType]: DynamicFormButtonTypes;
        [fields.sendToThirdPartyApi]: boolean;
        [fields.sendToApiType]: DynamicFormThirdPartyApiTypes;
        [fields.customApiEndpoint]: string;
        [fields.endpointRequiresAuth]: boolean;
        [fields.endpointUserId]: string;
        [fields.endpointPassword]: string;
        [fields.displayCustomApiResponse]: boolean;
        [fields.enableNewPricing]: boolean;
    };
}

const mapStateToProps = (state: CustomDynamicFormState) => {
    const dynamicFormData = state.xcentiumDynamicForms;
    const getCategoriesParameter = {
        maxDepth: 2,
        includeStartCategory: false,
    };
    return {
        dynamicFormData,
        currentLocation: getLocation(state),
        isAuthenticatedUser: state.context.session.isAuthenticated && !state.context.session.isGuest,
        signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        categoriesDataView: getCategoriesDataView(state, getCategoriesParameter),
        getCategoriesParameter,
        topLevelCategoryIds: getSubCategoryIds(state, emptyGuid),
    };
};

const mapDispatchToProps = {
    setDynamicFormFieldValue,
    loadCategories,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface DownloadPricingFormStyles {
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    wrapper: InjectableCss;
    accordianOuterWrapper?: InjectableCss;
    accordianInnerWrapper?: InjectableCss;
    accordianHiddenWrapper?: InjectableCss;
    option1Wrapper?: InjectableCss;
    option2Wrapper?: InjectableCss;
    emailAddressWrapper?: InjectableCss;
    checkboxWrapper?: InjectableCss;
    itemWrapper: InjectableCss;
    unauthenticatedWrapper: InjectableCss;
    unauthenticatedHeader: TypographyPresentationProps;
    loginButton: ButtonPresentationProps;
}

export const downloadPricingFormStyles: DownloadPricingFormStyles = {
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    wrapper: {
        css: css`
            width: 100%;
            margin-bottom: 2em;
        `,
    },
    option1Wrapper: {
        css: css`
            width: 100%;
        `,
    },
    option2Wrapper: {
        css: css`
            width: 100%;
        `,
    },
    accordianOuterWrapper: {
        css: css`
            width: 100%;
            margin-bottom: 2em;
        `,
    },
    accordianInnerWrapper: {
        css: css`
            margin-left: 20px;
            width: 100%;
        `,
    },
    accordianHiddenWrapper: {
        css: css`
            display: none;
        `,
    },
    emailAddressWrapper: {
        css: css`
            width: 100%;
            margin-bottom: 1em;
        `,
    },
    checkboxWrapper: {
        css: css`
            width: 100%;
        `,
    },
    itemWrapper: {
        css: css`
            width: 100%;
            padding-bottom: 10px;
            margin-bottom: 10px;
        `,
    },
    unauthenticatedWrapper: {
        css: css`
            width: 100%;
        `,
    },
    unauthenticatedHeader: {
        css: css`
            font-weight: 600;
        `,
    },
    loginButton: {
        css: css`
            background-color: ${getColor("secondary")};
        `,
    },
};

const styles = downloadPricingFormStyles;

type CategoryOption = {
    key: string;
    label: string;
    children?: CategoryOption[];
}
type SubCategoryOption = {
    key: string;
    label: string;
}

const DownloadPricingForm: FC<Props> = props => {
    const { id, isAuthenticatedUser, history, signInUrl, currentLocation, fields, dynamicFormData } = props;
    const { categoriesDataView, getCategoriesParameter, topLevelCategoryIds, loadCategories } = props;
    const {
        emailRecipients,
        successMessage,
        columns,
        enableRecaptcha,
        submitButtonText,
        buttonVariant,
        buttonShape,
        buttonType,
        sendToThirdPartyApi,
        sendToApiType,
        customApiEndpoint,
        endpointRequiresAuth,
        endpointUserId,
        endpointPassword,
        displayCustomApiResponse,
        enableNewPricing,
    } = fields;

    const [showOptions, setShowOptions] = useState<boolean>(false);
    const [categoryOptions, setCategoryOptions] = useState<CategoryOption[]>([]);

    useEffect(() => {
        if (!categoriesDataView.isLoading && !categoriesDataView.value) {
            loadCategories(getCategoriesParameter);
        }
    }, []);

    useEffect(() => {
        if (categoriesDataView && categoriesDataView.value && categoryOptions.length === 0) {
            const topLevelCategoryOptions = categoriesDataView.value
                .filter(c => 
                    topLevelCategoryIds?.includes(c.id) && 
                    c.subCategoryIds && 
                    c.subCategoryIds.length > 0
                )
                .sort(x => x.sortOrder)
                .map(topLevelCategory => {
                    // Create a container to hold the sub-category options.
                    const children = [] as SubCategoryOption[];
                    // Add the default "All" option.
                    children.push({ key: "all", label: "All"});
                    // Map the children for this TopLevelCategoryOption.
                    const _children = categoriesDataView.value!
                        .filter(cdv => topLevelCategory.subCategoryIds?.includes(cdv.id) && cdv.properties["sectionId"])
                        .sort(x => x.sortOrder)
                        .map(subCategory => {
                            const subCategoryOption = {
                                key: `${subCategory.name}|${subCategory.properties["sectionId"]}`,
                                label: subCategory.name,
                            };
                            return subCategoryOption;
                        });
                    // Join the two arrays.
                    const allChildren = children.concat(_children);
                    // Map the TopLevelCategoryOption.
                    const topLevelCategoryOption = {
                        key: topLevelCategory.name,
                        label: topLevelCategory.name,
                        children: allChildren,
                    } as CategoryOption;

                    return topLevelCategoryOption;
                });
            
            setCategoryOptions(topLevelCategoryOptions);
        }        
    }, [categoriesDataView]);

    const handleLoginButtonClick = (e: React.MouseEvent) => {
        const url = signInUrl || "/MyAccount/SignIn";
        const returnUrl = currentLocation.pathname + currentLocation.search;
        if (returnUrl === "/") {
            history.push(`${signInUrl}`);
        } else {
            history.push(`${signInUrl}?returnUrl=${encodeURIComponent(returnUrl)}&clientRedirect=true`);
        }

        e.preventDefault();
    };

    if (!isAuthenticatedUser) {
        return (
            <StyledWrapper {...styles.wrapper} data-test-selector="DownloadPricingForm_wrapper">
                <StyledWrapper {...styles.itemWrapper}>
                    <Typography {...styles.unauthenticatedHeader}>
                        You must be logged in to an account before you can access the Download Pricing Sheets forms.
                    </Typography>
                </StyledWrapper>
                <StyledWrapper {...styles.itemWrapper}>
                    <Typography>
                        Click the Login button to create an account or log in to your existing account.
                    </Typography>
                </StyledWrapper>
                <StyledWrapper {...styles.unauthenticatedWrapper}>
                    <Button {...styles.loginButton} onClick={handleLoginButtonClick}>
                        {translate("Sign In")}
                    </Button>
                </StyledWrapper>
            </StyledWrapper>
        );
    }

    const formName = "Download Pricing Form";
    const formIndex = determineFormIndex(formName, dynamicFormData).formIndex;

    const onPrevBoughtGroupChangeHandler = (newValue: string) => {
        switch (newValue) {
            case "prev":
                setShowOptions(false);
                break;
            case "custom":
                setShowOptions(true);
                break;
            case "all":
                setShowOptions(false);
                break;
            default:
                break;
        }
    };

    let finalAccordianInnerStyles = styles.accordianHiddenWrapper;
    if (formIndex > -1) {
        const prevBoughtIndex = dynamicFormData.forms[formIndex].fields.findIndex(f => f.key === "prev-bought");
        if (prevBoughtIndex > -1) {
            const currValue = dynamicFormData.forms[formIndex].fields[prevBoughtIndex].value;
            if (currValue && currValue === "custom") {
                finalAccordianInnerStyles = styles.accordianInnerWrapper;
            }
        }
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            <DynamicForm
                id={id}
                fields={{
                    formName,
                    emailRecipients,
                    submitButtonText,
                    successMessage,
                    buttonVariant,
                    buttonShape,
                    buttonType,
                    showResetButton: false,
                    enableRecaptcha,
                    sendToThirdPartyApi,
                    sendToApiType,
                    customApiEndpoint,
                    endpointRequiresAuth,
                    endpointUserId,
                    endpointPassword,
                    displayCustomApiResponse,
                }}
            >
                <StyledWrapper {...styles.option1Wrapper}>
                    <DynamicFormRadioGroup
                        onGroupChangeHandler={onPrevBoughtGroupChangeHandler}
                        fields={{
                            key: "prev-bought",
                            label: "Determine which categories to include",
                            isRequired: true,
                            columns: 1,
                            topics: [
                                {
                                    fields: {
                                        label: "All products in catalog",
                                        key: "all"
                                    },
                                },
                                {
                                    fields: {
                                        label: "Bought in the last twelve months",
                                        key: "prev",
                                    },
                                },
                                { 
                                    fields: { 
                                        label: "Custom", 
                                        key: "custom" 
                                    }, 
                                },
                            ],
                        }}
                    />
                </StyledWrapper>
                <StyledWrapper {...styles.accordianOuterWrapper}>
                    <StyledWrapper {...finalAccordianInnerStyles}>
                        <Accordion headingLevel={1}>
                            {categoryOptions.map((option, index) => {
                                const topics = option.children!.map(subOption => {
                                    return {
                                        fields: {
                                            key: subOption.key,
                                            isCheckedByDefault: false,
                                            labelPosition: DynamicFormCheckboxGroupLabelPositionsRight,
                                            label: subOption.label,
                                            changeTrueFalseToYesNo: false,
                                        },
                                    };
                                });

                                return (
                                    <ManagedAccordionSection
                                        key={`accordion_${index}`}
                                        title={option.label}
                                        initialExpanded={index === 0}
                                    >
                                        <DynamicFormCheckboxGroup
                                            fields={{
                                                key: option.key,
                                                hasLabel: false,
                                                label: option.label,
                                                columns,
                                                requireOneSelection: false,
                                                sendOnlyIfChecked: false,
                                                topics,
                                            }}
                                        />
                                    </ManagedAccordionSection>
                                );
                            })}
                        </Accordion>
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...styles.emailAddressWrapper}>
                    <DynamicFormTextField
                        fields={{
                            key: "email",
                            labelPosition: DynamicFormTextFieldLabelPositionsTop,
                            label: "Email Address",
                            hintText: "",
                            isRequired: true,
                            enableMinLength: false,
                            minLength: 0,
                            enableMaxLength: false,
                            maxLength: 100,
                            validateAsPhoneNumber: false,
                            validateAsEmailAddress: true,
                        }}
                    />
                </StyledWrapper>
                {enableNewPricing && (                    
                    <StyledWrapper {...styles.checkboxWrapper}>
                        <DynamicFormCheckbox
                            fields={{
                                key: "preview_new_pricing",
                                group: "",
                                labelPosition: DynamicFormCheckboxLabelPositionsRight,
                                label: "Preview New Pricing",
                                isRequired: false,
                                isCheckedByDefault: false,
                                changeTrueFalseToYesNo: false,
                                hintText: "",
                            }}
                        />
                    </StyledWrapper>
                )}
            </DynamicForm>
        </StyledWrapper>
    );
};

const formTab = {
    displayName: "Form",
    sortOrder: 0,
};

const buttonTab = {
    displayName: "Button",
    sortOrder: 1,
};

const actionTab = {
    displayName: "Actions",
    sortOrder: 2,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(DownloadPricingForm)),
    definition: {
        group: "Basic",
        displayName: "Download Pricing Form",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.emailRecipients,
                displayName: "Email recipients",
                editorTemplate: "MultilineTextField",
                fieldType: "General",
                defaultValue: "testaccount@insitesoft.com",
                isRequired: true,
                regularExpression: DynamicFormEmailRegExp,
                tab: formTab,
                sortOrder: 1,
            },
            {
                name: fields.successMessage,
                displayName: "Submit Success Message",
                editorTemplate: "RichTextField",
                fieldType: "Translatable",
                defaultValue: "Thank you for your inquiry. A customer service representative will contact you shortly.",
                tab: formTab,
                sortOrder: 2,
            },
            {
                name: fields.enableNewPricing,
                displayName: "Enable New Pricing Option",
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: false,
                sortOrder: 3,
                tab: formTab,
            },
            {
                name: fields.columns,
                displayName: "Columns",
                editorTemplate: "IntegerField",
                defaultValue: 1,
                fieldType: "General",
                isRequired: true,
                min: 1,
                max: 3,
                tooltip: "How many columns to use to display the checkbox group. Min 1. Max 3.",
                sortOrder: 4,
                tab: formTab,
            },
            {
                name: fields.enableRecaptcha,
                displayName: "Include ReCaptcha",
                tooltip: "Enabling this setting adds a ReCaptcha element to the form which must be clicked to submit.",
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: false,
                sortOrder: 5,
                tab: formTab,
            },
            {
                name: fields.submitButtonText,
                displayName: "Submit Button Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Submit",
                tab: buttonTab,
                sortOrder: 100,
            },
            {
                name: fields.buttonVariant,
                displayName: "Select Button Variant",
                editorTemplate: "DropDownField",
                defaultValue: "primary",
                fieldType: "General",
                options: [
                    {
                        displayName: "Primary",
                        value: "primary",
                    },
                    {
                        displayName: "Secondary",
                        value: "secondary",
                    },
                    {
                        displayName: "Tertiary",
                        value: "tertiary",
                    },
                ],
                hideEmptyOption: true,
                isRequired: true,
                tab: buttonTab,
                sortOrder: 105,
            },
            {
                name: fields.buttonShape,
                displayName: "Select Button Shape",
                editorTemplate: "DropDownField",
                defaultValue: "rectangle",
                fieldType: "General",
                options: [
                    {
                        displayName: "Rectangle",
                        value: "rectangle",
                    },
                    {
                        displayName: "Pill",
                        value: "pill",
                    },
                    {
                        displayName: "Rounded",
                        value: "rounded",
                    },
                ],
                hideEmptyOption: true,
                isRequired: true,
                tab: buttonTab,
                sortOrder: 110,
            },
            {
                name: fields.buttonType,
                displayName: "Select Button Type",
                editorTemplate: "DropDownField",
                defaultValue: "solid",
                fieldType: "General",
                options: [
                    {
                        displayName: "Solid",
                        value: "solid",
                    },
                    {
                        displayName: "Outline",
                        value: "outline",
                    },
                ],
                hideEmptyOption: true,
                isRequired: true,
                tab: buttonTab,
                sortOrder: 115,
            },
            {
                name: fields.sendToThirdPartyApi,
                displayName: "Upload FormData to 3rd Party System",
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: false,
                sortOrder: 200,
                tab: actionTab,
            },
            {
                name: fields.sendToApiType,
                displayName: "Select 3rd Party System Type",
                editorTemplate: "DropDownField",
                defaultValue: DynamicFormThirdPartyApiTypesCustom,
                fieldType: "General",
                options: [
                    {
                        displayName: "Custom API Endpoint",
                        value: DynamicFormThirdPartyApiTypesCustom,
                    },
                ],
                hideEmptyOption: true,
                isRequired: true,
                isVisible: widget => widget.fields.sendToThirdPartyApi,
                tab: actionTab,
                sortOrder: 210,
            },
            {
                name: fields.customApiEndpoint,
                displayName: "Custom API Endpoint URL",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
                isRequired: true,
                isVisible: widget => widget.fields.sendToThirdPartyApi && widget.fields.sendToApiType === "custom-api",
                tab: actionTab,
                sortOrder: 220,
            },
            {
                name: fields.endpointRequiresAuth,
                displayName: "Upload Requires Authentication",
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: false,
                isVisible: widget => widget.fields.sendToThirdPartyApi,
                tab: actionTab,
                sortOrder: 221,
            },
            {
                name: fields.endpointUserId,
                displayName: "User Id",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
                isRequired: true,
                isVisible: widget => widget.fields.endpointRequiresAuth,
                tab: actionTab,
                sortOrder: 222,
            },
            {
                name: fields.endpointPassword,
                displayName: "Password",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
                isRequired: true,
                isVisible: widget => widget.fields.endpointRequiresAuth,
                tab: actionTab,
                sortOrder: 223,
            },
            {
                name: fields.displayCustomApiResponse,
                displayName: "Display results from API below Form for testing",
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: false,
                sortOrder: 230,
                tab: actionTab,
            },
        ],
    },
};

export default widgetModule;
